import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Container, Row, Col, Card, CardBody, Input, Button, Table,
    Modal, ModalHeader, ModalBody, Form, FormGroup, Label, InputGroup,
    FormFeedback, Spinner, Pagination, PaginationItem, PaginationLink,
    Badge
} from 'reactstrap';
import {
    FaSearch, FaBarcode, FaCube, FaBoxes, FaUser, FaPlus, FaTimes,
    FaAngleDoubleLeft, FaAngleLeft, FaAngleRight, FaAngleDoubleRight,
    FaFileExcel, FaEye, FaEdit, FaTrash, FaSave,
    FaFilter,
    FaList,
    FaLock
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import moment from 'moment';
import { productActions } from '../../sagas/productSlice';
import { debounce } from 'lodash';
import { inventoryActions } from '../../sagas/inventorySlice';
import * as XLSX from 'xlsx';

const InventoryPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Product Search and Selection
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const {
        products,
        loading,
        totalPages,
        totalElements,
        pageNumber,
        pageSize,
        error,
    } = useSelector((state) => state.products); const [productsLoading, setProductsLoading] = useState(false);
    const [productsPagination, setProductsPagination] = useState({ currentPage: 1, pageSize: 10 });
    const [productsTotalElements, setProductsTotalElements] = useState(0);
    const searchInputRef = useRef(null);
    const [isSearching, setIsSearching] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const { fullName, businessName } = useSelector(state => state.auth);
    const [savedInventory, setSavedInventory] = useState(null);
    const [isInventoryClosed, setIsInventoryClosed] = useState(false);

    // Supplier Filter
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [supplierModal, setSupplierModal] = useState(false);
    const [criteria, setSearchCriteria] = useState({ query: '' });
    const [numberOfItems, setNumberOfItems] = useState(0);

    // Inventory Sheet
    const [inventoryDate, setInventoryDate] = useState(moment().format('YYYY-MM-DD'));
    const [inventoryItems, setInventoryItems] = useState([]);

    // Modal State
    const [productModal, setProductModal] = useState(false);
    const [inventoryListModal, setInventoryListModal] = useState(false);
    const [inventories, setInventories] = useState([]);

    useEffect(() => {

    }, [savedInventory]);
    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [products]);

    useEffect(() => {
        let payload = {
            criteria,
            page: currentPage,
            size: pageSize,
        };

        dispatch(productActions.search(payload));
        loadInventories();
    }, [currentPage]);


    useEffect(() => {
        // Set the total number of items only once, on initial load
        if (totalElements && numberOfItems === 0) {
            setNumberOfItems(totalElements);
        }
    }, [totalElements, numberOfItems]);

    const debouncedSearch = useCallback(
        debounce((query) => {
            setIsSearching(true);

            dispatch(productActions.search({
                criteria: {
                    query,
                    supplierId: selectedSupplier ? selectedSupplier.id : null
                },
                page: 1,
                size: pageSize,
                onSuccess: (items) => {
                    setIsSearching(false)
                    // Check for exact barcode match
                    const searchLower = query.toLowerCase();
                    const exactMatch = items?.find(p =>
                        p.barcode?.toLowerCase() === searchLower ||
                        p.code?.toLowerCase() === searchLower ||
                        p.description?.toLowerCase() === searchLower
                    );

                    if (exactMatch) {
                        // Add to cart directly
                        handleProductSelect(exactMatch);
                        setSearchTerm(''); // Clear search input after adding
                    }
                    setTimeout(() => {
                        searchInputRef.current?.focus();
                    }, 0);
                }
            }));
        }, 1000), // Wait 500ms after last keystroke
        []
    );

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };

    const handleProductSelect = (product) => {
        const hasOpenInventory = inventories.some(inventory => !inventory.closed);

        if ((!isInventoryClosed && inventoryItems.length > 0) || (inventoryItems.length === 0 && !hasOpenInventory)) {
            setSelectedProduct(product);
            setProductModal(false);
            // Add product to inventory sheet if not already present
            setInventoryItems(prevItems => {
                if (prevItems.find(item => item.product.id === product.id)) {
                    return prevItems; // Product already exists, don't add
                } else {
                    return [...prevItems, {
                        product: product,
                        stockReel: product.stock,
                        commentaire: ''
                    }];
                }
            });
        } else {
            Swal.fire({
                title: t('inventory.closeCurrentInventory'),
                text: t('inventory.closeCurrentInventoryWarning'),
                icon: 'warning',
                confirmButtonText: t('common.ok')
            });
        }
    };

    const handleInventoryDateChange = (e) => {
        setInventoryDate(e.target.value);
    };

    const handleStockReelChange = (index, value) => {
        if (isInventoryClosed) return;
        const newInventoryItems = [...inventoryItems];
        newInventoryItems[index].stockReel = parseInt(value);
        setInventoryItems(newInventoryItems);
    };

    const handleCommentaireChange = (index, value) => {
        if (isInventoryClosed) return;
        const newInventoryItems = [...inventoryItems];
        newInventoryItems[index].commentaire = value;
        setInventoryItems(newInventoryItems);
    };

    const calculateValeurUnitaire = (product) => {
        return product.buyingPrice || 0; // Use buying price as unit value
    };

    const calculateStockTheorique = (product) => {
        return product.stock || 0; // Use product stock as theorical stock
    };

    const calculateValeurStockReel = (item) => {
        return item.stockReel * calculateValeurUnitaire(item.product);
    };

    const calculateEcartQuantite = (item) => {
        return item.stockReel - calculateStockTheorique(item.product);
    };

    const calculateEcartValeur = (item) => {
        return formatPrice(calculateValeurStockReel(item) - (calculateStockTheorique(item.product) * calculateValeurUnitaire(item.product)));
    };

    const handleSubmitInventory = () => {
        const isEdit = savedInventory !== null;

        Swal.fire({
            title: t('inventory.confirmSubmit'),
            text: t('inventory.submitWarning'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('common.yesSubmit'),
            cancelButtonText: t('common.cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                // Here you would typically send the inventory data to your backend
                let payload = {
                    id: savedInventory ? savedInventory.id : null,
                    data: {
                        inventoryDate,
                        totalStockValue: calculateTotalStockValue(),
                        totalItems: inventoryItems.length,
                        inventoryItems: inventoryItems.map(item => ({
                            productId: item.product.id,
                            stockReel: item.stockReel,
                            stockTheorique: calculateStockTheorique(item.product),
                            stockValeurUnitaire: calculateValeurUnitaire(item.product),
                            stockEcartQuantite: calculateEcartQuantite(item),
                            commentaire: item.commentaire
                        }))
                    },
                    onSuccess: (data) => {
                        setSavedInventory(data); // Save the returned data to state
                        Swal.fire(
                            t(`inventory.submitted${isEdit ? 'Edit' : ''}`), // Use different translation key for edit
                            t(`inventory.submitSuccess${isEdit ? 'Edit' : ''}`), // Use different translation key for edit
                            'success'
                        );
                    },
                    onError: () => {
                        Swal.fire(
                            t('inventory.error'),
                            t('inventory.submitError'),
                            'error'
                        );
                    }
                };
                dispatch(inventoryActions[isEdit ? 'updateInventory' : 'addInventory'](payload)); // Dispatch different action based on edit or add
            }
        });
    };

    const handleRemoveInventoryItem = (productId) => {
        if (isInventoryClosed) return;
        setInventoryItems(inventoryItems.filter(item => item.product.id !== productId));
    };

    const calculateTotalStockValue = () => {
        let total = 0;
        inventoryItems.forEach(item => {
            total += calculateValeurStockReel(item);
        });
        return total.toFixed(2); // Format to 2 decimal places
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Update price formatting function
    const formatPrice = (amount) => {
        const formattedPrice = new Intl.NumberFormat('fr-MA', {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
        return `${formattedPrice} DH`;
    };

    const loadInventories = () => {
        dispatch(inventoryActions.findAll({
            onSuccess: (data) => {
                setInventories(data);
            },
            onError: () => {
                Swal.fire(
                    t('inventory.error'),
                    t('inventory.loadError'),
                    'error'
                );
            }
        }));
    }

    const handleOpenInventoryListModal = () => {
        loadInventories();
        setInventoryListModal(true);
    };

    const handleSelectInventory = (inventory) => {
        setSavedInventory(inventory);
        setInventoryDate(inventory.inventoryDate);
        setInventoryItems(inventory.inventoryItems.map(item => ({
            product: item.product,
            stockReel: item.stockReel,
            commentaire: item.commentaire
        })));
        setInventoryListModal(false);
        setIsInventoryClosed(inventory.closed);
    };

    const handleCloseInventory = () => {
        Swal.fire({
            title: t('inventory.confirmClose'),
            text: t('inventory.closeWarning'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('common.yesClose'),
            cancelButtonText: t('common.cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                if (savedInventory) {
                    dispatch(inventoryActions.closeInventory({
                        id: savedInventory.id,
                        onSuccess: () => {
                            setIsInventoryClosed(true);
                            setInventoryItems([]);
                            loadInventories();
                            Swal.fire(
                                t('inventory.closed'),
                                t('inventory.closeSuccess'),
                                'success'
                            );
                        },
                        onError: () => {
                            Swal.fire(
                                t('inventory.error'),
                                t('inventory.closeError'),
                                'error'
                            );
                        }
                    }));
                }
            }
        });
    };

    const handleNewInventory = () => {
        const hasOpenInventory = inventories.some(inventory => !inventory.closed);
        if (hasOpenInventory) {
            Swal.fire({
                title: t('inventory.closeCurrentInventory'),
                text: t('inventory.closeCurrentInventoryWarning'),
                icon: 'warning',
                confirmButtonText: t('common.ok')
            });
            return;
        }
        setInventoryItems([]);
        setInventoryDate(moment().format('YYYY-MM-DD'));
        setSavedInventory(null);
        setIsInventoryClosed(false);
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            dispatch(productActions.search({
                criteria: { searchTerm },
                page: 1,
                size: pageSize
            }));

        }
    };

    const handleExportToExcel = () => {
        // Create a new workbook
        const wb = XLSX.utils.book_new();

        // Prepare the header row with inventory date and stock value
        const headerRow = [
            `${t('inventory.inventoryDate')}: ${inventoryDate}`,
            `${t('inventory.currentStockValue')}: ${formatPrice(calculateTotalStockValue())}`,
            `${t('inventory.responsible')}: ${fullName}`,
        ];

        // Prepare the data for Excel
        const data = [
            headerRow, // First row with inventory date and stock value
            [
                t('inventory.codeArticle'),
                t('inventory.description'),
                t('inventory.stockTheorique'),
                t('inventory.valeurUnitaire'),
                t('inventory.stockReel'),
                t('inventory.valeurStockReel'),
                t('inventory.ecartQuantite'),
                t('inventory.ecartValeur'),
                t('inventory.commentaire')
            ], // Header row
            ...inventoryItems.map(item => [
                item.product.code,
                item.product.description,
                calculateStockTheorique(item.product),
                calculateValeurUnitaire(item.product),
                item.stockReel,
                calculateValeurStockReel(item),
                calculateEcartQuantite(item),
                calculateEcartValeur(item),
                item.commentaire
            ]) // Inventory items
        ];

        // Create a worksheet
        const ws = XLSX.utils.aoa_to_sheet(data);

        // Add the worksheet to the workbook
        XLSX.utils.book_append_sheet(wb, ws, t('inventory.inventorySheet'));

        // Generate the Excel file
        XLSX.writeFile(wb, `${t('inventory.inventorySheet')}_${inventoryDate}.xlsx`);
    };

    return (
        <Container fluid>
            <Row className="mb-3">
                <Col>
                    <Card className="shadow-sm border-0">
                        <CardBody className="py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h4 className="mb-0 d-flex align-items-center">
                                    <FaBoxes className="text-primary me-2" />
                                    {t('inventory.pageTitle')}
                                </h4>
                                <div>
                                    <Button color="primary" onClick={handleOpenInventoryListModal} className='me-1' >
                                        <FaList className="me-1" />
                                        {t('inventory.allInventories')}
                                    </Button>
                                    <Button color="success" onClick={handleNewInventory} className='me-1'>
                                        <FaPlus className="me-1" />
                                        {t('inventory.newInventory')}
                                    </Button>

                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <Card className="shadow-sm h-100">
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">{t('inventory.inventorySheet')}</h5>
                                <Badge color={isInventoryClosed ? "danger" : "success"} className="me-2">
                                    {isInventoryClosed ? t('inventory.closed') : t('inventory.open')}
                                </Badge>
                                <div>
                                    <span className="me-3">{t('inventory.inventoryDate')}:</span>
                                    <Input
                                        type="date"
                                        value={inventoryDate}
                                        onChange={handleInventoryDateChange}
                                        style={{ width: '150px', display: 'inline-block' }}
                                        disabled={isInventoryClosed}

                                    />
                                </div>


                            </div>
                            <div className="position-relative flex-grow-1 me-3 mb-3">
                                <Input
                                    innerRef={searchInputRef}
                                    type="text"
                                    placeholder={t('product.searchFocusHint')}
                                    value={searchTerm}
                                    onKeyDown={handleSearchKeyDown}
                                    onChange={handleSearchChange}
                                    className={`ps-4 pe-5 ${isSearching ? 'searching' : ''}`}
                                    title={t('product.searchShortcut')}
                                />
                                <FaSearch
                                    style={{
                                        position: 'absolute',
                                        left: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#adb5bd'
                                    }}
                                    size={14}
                                />
                                <FaBarcode
                                    style={{
                                        position: 'absolute',
                                        right: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#adb5bd'
                                    }}
                                    size={20}
                                    title={t('product.scanHint')}
                                />
                            </div>

                            <Table bordered hover>
                                <thead>
                                    <tr>
                                        <th>{t('inventory.codeArticle')}</th>
                                        <th>{t('inventory.description')}</th>
                                        <th>{t('inventory.stockTheorique')}</th>
                                        <th>{t('inventory.stockReel')}</th>
                                        <th>{t('inventory.valeurUnitaire')}</th>
                                        {/* <th>{t('inventory.valeurStockReel')}</th> */}
                                        <th>{t('inventory.ecartQuantite')}</th>
                                        {/* <th>{t('inventory.ecartValeur')}</th> */}
                                        {/* <th>{t('inventory.commentaire')}</th> */}
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {inventoryItems.map((item, index) => (
                                        <tr key={item.product.id}>
                                            <td>{item.product.code}</td>
                                            <td>{item.product.description}</td>
                                            <td>{calculateStockTheorique(item.product)}</td>
                                            <td>
                                                <Input
                                                    type="number"
                                                    value={item.stockReel}
                                                    onChange={(e) => handleStockReelChange(index, e.target.value)}
                                                    disabled={isInventoryClosed}
                                                />
                                            </td>
                                            <td>{calculateValeurUnitaire(item.product)}</td>

                                            {/* <td>{formatPrice(calculateValeurStockReel(item))}</td> */}
                                            <td>{calculateEcartQuantite(item)}</td>
                                            {/* <td>{calculateEcartValeur(item)}</td> */}
                                            {/* <td>
                                                <Input
                                                    type="text"
                                                    value={item.commentaire}
                                                    onChange={(e) => handleCommentaireChange(index, e.target.value)}
                                                    disabled={isInventoryClosed}
                                                />
                                            </td> */}
                                            <td>
                                                <Button
                                                    color="danger"
                                                    size="sm"
                                                    onClick={() => handleRemoveInventoryItem(item.product.id)}
                                                    disabled={isInventoryClosed}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <div>
                                    <strong style={{ fontSize: '1.5em' }}>
                                        {t('inventory.currentStockValue')}: {formatPrice(calculateTotalStockValue())}
                                    </strong>
                                </div>
                                <div>
                                    <strong style={{ fontSize: '1.5em' }}>
                                        {t('inventory.totalItems')}: {inventoryItems.length} / {numberOfItems}
                                    </strong>
                                </div>
                            </div>
                            <Button color="success" onClick={handleSubmitInventory}
                                disabled={isInventoryClosed || inventoryItems.length === 0 || inventoryItems.some(item => item.stockReel === 0)}
                            >
                                {t('common.save')} <FaSave className="ms-1" />
                            </Button>
                            <Button color="warning" onClick={handleCloseInventory} disabled={isInventoryClosed || !savedInventory} className="ms-2">
                                <FaLock className="me-1" />
                                {t('inventory.closeInventory')}
                            </Button>
                            <Button color="info" onClick={handleExportToExcel} className="ms-2" disabled={inventoryItems.length === 0}>
                                <FaFileExcel className="me-1" />
                                {t('common.exportExcel')}
                            </Button>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Inventory List Modal */}
            <Modal isOpen={inventoryListModal} toggle={() => setInventoryListModal(false)} size='lg'>
                <ModalHeader toggle={() => setInventoryListModal(false)}>
                    {t('inventory.selectInventory')}
                </ModalHeader>
                <ModalBody>
                    {inventories.length === 0 ? (
                        <div className="text-center py-5">
                            {t('inventory.noInventories')}
                        </div>
                    ) : (
                        <Table bordered hover>
                            <thead>
                                <tr>
                                    <th>{t('inventory.inventoryDate')}</th>
                                    <th>{t('inventory.totalItems')}</th>
                                    <th>{t('inventory.status')}</th> {/* Add status header */}
                                </tr>
                            </thead>
                            <tbody>
                                {inventories.map((inventory) => (
                                    <tr key={inventory.id} onClick={() => handleSelectInventory(inventory)}>
                                        <td>{inventory.inventoryDate}</td>
                                        <td>{inventory.totalItems} / {totalElements}</td> {/* UPDATED */}
                                        <td>{
                                            <Badge color={inventory.closed ? "danger" : "success"} className="me-2">
                                                {inventory.closed ? t('inventory.closed') : t('inventory.open')}
                                            </Badge>
                                        }</td> {/* Display status */}

                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default InventoryPage;